import React, { useCallback, useMemo } from 'react';
import { Autocomplete, AutocompleteProps, CircularProgress, TextField } from '@mui/material';
import { SxProps, Theme } from '@mui/material/styles';

/** Minimal shape a record needs to be selectable without a custom `getOptionId`. */
export interface Identifiable {
  id?: string;
}

type PassthroughProps<T> = Partial<
  Pick<
    AutocompleteProps<T, false, false, false>,
    'renderOption' | 'getOptionDisabled' | 'groupBy' | 'openOnFocus' | 'autoHighlight' | 'noOptionsText' | 'loadingText' | 'slotProps'
  >
>;

export interface SearchableSelectProps<T> extends PassthroughProps<T> {
  /** Records to choose from. Callers may pass a pre-filtered subset. */
  options: T[];
  /** Id of the selected record; `''` or `null` when nothing is chosen. */
  value: string | null;
  /** Called with the chosen record's id (`''` when cleared) and the record itself. */
  onChange: (id: string, option: T | null) => void;
  /** Text shown for a record, both in the list and in the closed box. */
  getOptionLabel: (option: T) => string;
  /** Pulls the id out of a record. Defaults to `option.id`. */
  getOptionId?: (option: T) => string;
  /**
   * The selected record when it is not present in `options` — a value loaded
   * from an older document, or a server-searched picker whose current page no
   * longer contains the selection. Without it the box would render empty.
   */
  selectedOption?: T | null;
  label?: string;
  placeholder?: string;
  size?: 'small' | 'medium';
  /**
   * Defaults to true so the control fills its own cell. In a filter row the
   * caller constrains that cell (`flex: '1 1 260px'` + a `maxWidth`) rather
   * than turning this off — see the frontend UI convention in CLAUDE.md.
   */
  fullWidth?: boolean;
  disabled?: boolean;
  required?: boolean;
  error?: boolean;
  helperText?: string;
  /** Show the clear (×) button. Defaults to true. */
  clearable?: boolean;
  sx?: SxProps<Theme>;
  /** Spinner in the box while the caller is fetching. */
  loading?: boolean;
  /**
   * Turn off the built-in substring filter — for pickers that search on the
   * server and hand back an already-filtered `options` array.
   */
  disableClientFilter?: boolean;
  /** Fires with the typed text (`''` on clear); pair with `disableClientFilter`. */
  onInputChange?: (value: string) => void;
  'data-testid'?: string;
  /** Help-mode key; reaches the DOM through the pass-through props. */
  'data-help'?: string;
}

const defaultGetOptionId = <T,>(option: T): string =>
  String((option as Identifiable)?.id ?? '');

/**
 * The app's single searchable dropdown. Replaces the `<FormControl><Select>`
 * + `<MenuItem>` blocks used across every module: the user can type part of a
 * record's name to narrow a long list instead of scrolling it.
 *
 * The value in and out is the record's id, so a call site that already stores
 * an id swaps over without touching its state.
 *
 * ```tsx
 * <SearchableSelect
 *   options={customers}
 *   value={customerId}
 *   onChange={setCustomerId}
 *   getOptionLabel={c => c.name}
 *   label="Customer"
 *   required
 * />
 * ```
 *
 * Short fixed-choice dropdowns (status, month, a handful of enum values) stay
 * plain `<Select>`s — a search box there costs a click and gains nothing.
 *
 * Pickers that search on the server (see `EmployeeAutocomplete`) render through
 * this component too, with `disableClientFilter`, `loading` and `onInputChange`.
 */
export function SearchableSelect<T>({
  options,
  value,
  onChange,
  getOptionLabel,
  getOptionId = defaultGetOptionId,
  selectedOption,
  label,
  placeholder,
  size = 'small',
  fullWidth = true,
  disabled = false,
  required,
  error,
  helperText,
  clearable = true,
  sx,
  loading = false,
  disableClientFilter = false,
  onInputChange,
  renderOption,
  ...rest
}: SearchableSelectProps<T>) {
  // Keep the selection reachable even when it is missing from the current list,
  // otherwise MUI renders an empty box for a value the record still holds.
  const mergedOptions = useMemo(() => {
    if (!selectedOption) return options;
    const selectedId = getOptionId(selectedOption);
    return options.some(o => getOptionId(o) === selectedId)
      ? options
      : [selectedOption, ...options];
  }, [options, selectedOption, getOptionId]);

  const selected = useMemo(() => {
    if (!value) return null;
    return mergedOptions.find(o => getOptionId(o) === value) ?? null;
  }, [mergedOptions, value, getOptionId]);

  const handleInputChange = useCallback(
    (_event: React.SyntheticEvent, next: string, reason: string) => {
      if (!onInputChange) return;
      if (reason === 'input') onInputChange(next);
      else if (reason === 'clear') onInputChange('');
    },
    [onInputChange]
  );

  return (
    <Autocomplete<T, false, false, false>
      options={mergedOptions}
      value={selected}
      onChange={(_event, next) => onChange(next ? getOptionId(next) : '', next)}
      onInputChange={handleInputChange}
      getOptionLabel={getOptionLabel}
      isOptionEqualToValue={(option, current) => getOptionId(option) === getOptionId(current)}
      {...(disableClientFilter ? { filterOptions: (x: T[]) => x } : {})}
      disabled={disabled}
      // Cast: MUI ties `disableClearable` to the generic that decides whether
      // `value` may be null. This control is always nullable, so the flag is
      // applied as a plain runtime prop.
      {...(clearable ? {} : { disableClearable: true as unknown as false })}
      loading={loading}
      fullWidth={fullWidth}
      size={size}
      sx={sx}
      autoHighlight
      // Key rows by record id. MUI's default keys them by label, so two records
      // sharing a name (two customers called "Delta Textiles") collide: React
      // leaves ghost rows behind while filtering and a click picks the wrong one.
      renderOption={
        renderOption ??
        ((props, option) => {
          const { key: _labelKey, ...liProps } = props as typeof props & { key?: React.Key };
          return (
            <li {...liProps} key={getOptionId(option) || getOptionLabel(option)}>
              {getOptionLabel(option)}
            </li>
          );
        })
      }
      renderInput={params => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          required={required}
          error={error}
          helperText={helperText}
          inputProps={{
            ...params.inputProps,
            // Clicking back into a filled box selects its label, so typing replaces it
            // and starts a fresh search (MUI only does this on the first focus, and a
            // second click dropped the caret mid-label: "Single Jersey 1Twill80 GSM").
            onClick: (event: React.MouseEvent<HTMLInputElement>) => {
              (params.inputProps as { onClick?: React.MouseEventHandler<HTMLInputElement> }).onClick?.(event);
              const input = event.currentTarget;
              if (selected && input.value === getOptionLabel(selected)) input.select();
            },
          }}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      {...rest}
    />
  );
}

export default SearchableSelect;
